import i18next from 'i18next';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { sortByName } from '../../../utils/sort';
import { unitPhaseTypes } from '../../../config/commons';
import { getUserSession } from '../../../utils/userSession';
import { axiosClient as axios } from '../../../utils/axios_instance';
import { openSuccessNotification } from '../../../common/notification/Notification';
import { IntegrationAQ1State, Tank, Campus, Module, IntegratedPond } from '../interfaces';
import { INTEGRATED_PONDS_URL, CAMPUS_URL, MODULES_URL, TANKS_URL } from '../../../config/config.api';

const initialState: IntegrationAQ1State = {
  isLoading: false,
  isUpdating: false,
  showModal: false,
  integratedPonds: [],
  campuses: [],
};

export const integrationAQ1Slice = createSlice({
  name: 'integrationAQ1',
  initialState,
  reducers: {
    setIsLoading: (state: IntegrationAQ1State, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsUpdating: (state: IntegrationAQ1State, action: PayloadAction<boolean>) => {
      state.isUpdating = action.payload;
    },
    setShowModal: (state: IntegrationAQ1State, action: PayloadAction<boolean>) => {
      state.showModal = action.payload;
    },
    setIntegratedPonds: (state: IntegrationAQ1State, action: PayloadAction<IntegratedPond[]>) => {
      state.integratedPonds = action.payload;
    },
    setCampuses: (state: IntegrationAQ1State, action: PayloadAction<Campus[]>) => {
      state.campuses = action.payload;
    },
  },
});

export const {
  setIsLoading,
  setIsUpdating,
  setShowModal,
  setIntegratedPonds,
  setCampuses,
} = integrationAQ1Slice.actions;

export const fetchCampuses = () => async (dispatch: Function) => {
  const userSession = getUserSession();

  const params = {
    $limit: -1,
    companyId: userSession.companyId,
    phaseType: unitPhaseTypes.PRODUCTION,
    $select: ['name'],
  };

  try {
    const response = await axios.get<Campus[]>(CAMPUS_URL, { params });
    const campuses = response.data;
    campuses.sort(sortByName);
    dispatch(setCampuses(campuses));
  } catch (error) {
    console.log(error);
  }
};

export const fetchModules = async (props: {campusId: string;}) => {
  const { campusId } = props;

  const params = {
    $limit: -1,
    campusId,
    active: true,
    $select: ['name'],
  };

  const response = await axios.get<Module[]>(MODULES_URL, { params });
  const module = response.data;
  module.sort(sortByName);
  return module;
};

export const fetchTanks = async (props: {campusId: string; moduleId: string;}) => {
  const { campusId, moduleId } = props;

  const params = {
    $limit: -1,
    campusId,
    moduleId,
    active: true,
    $select: ['name'],
  };

  const response = await axios.get<Tank[]>(TANKS_URL, { params });
  const ponds = response.data;
  ponds.sort(sortByName);
  return ponds;
};

export const fetchIntegratedAQ1Ponds = (props: {externalAccessId: string;}) => async (dispatch: Function) => {
  const { externalAccessId } = props;
  dispatch(setIsLoading(true));

  const url = `${INTEGRATED_PONDS_URL}/${externalAccessId}`;

  try {
    const response = await axios.get<IntegratedPond[]>(url);
    dispatch(setIntegratedPonds(response.data));
  } catch (error) {
    console.log(error?.response);
  }

  dispatch(setIsLoading(false));
};

export const updateIntegratedAQ1Ponds = (props: {integratedPonds: IntegratedPond[]; onSuccess?: Function;}) => async (dispatch: Function) => {
  const { integratedPonds, onSuccess } = props;

  if (onSuccess) {
    dispatch(setIsUpdating(true));
  }

  const body = {
    integratedPonds
  };

  try {
    await axios.patch(INTEGRATED_PONDS_URL, body);

    if (onSuccess) {
      onSuccess();
      openSuccessNotification(i18next.t('integrations.poolMapping.success'));
    }
  } catch (error) {
    console.log(error?.response);
  }

  if (onSuccess) {
    dispatch(setIsUpdating(false));
  }
};

export default integrationAQ1Slice.reducer;
